import { Component, type ErrorInfo, type ReactNode } from 'react'

/**
 * The fence around one custom-module component on a CORE page (E5 spec
 * §7.2). A render fault, or a chunk that fails to load, becomes `fallback`
 * instead of blanking the page. `resetKey` is what the slot is showing (the
 * record, the task): when it changes, a failed slot tries again.
 */
export class SlotBoundary extends Component<
  { name: string; resetKey?: string; fallback: ReactNode; children: ReactNode },
  { failed: boolean }
> {
  state = { failed: false }

  static getDerivedStateFromError() {
    return { failed: true }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error(`Custom slot '${this.props.name}' failed to render:`, error, info.componentStack)
  }

  componentDidUpdate(previous: { resetKey?: string }) {
    if (this.state.failed && previous.resetKey !== this.props.resetKey) this.setState({ failed: false })
  }

  render() {
    return this.state.failed ? this.props.fallback : this.props.children
  }
}
